import React, { useState, useEffect } from 'react';
import { getActiveRoute, startRoute, closeRoute, updateLocation, markOrderFailedV2, correctOrderAddress, updateOrderAddress } from '../services/routeService';
import { printRouteSheet } from '../utils/printRouteSheet';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
    Navigation,
    MapPin,
    Package,
    DollarSign,
    Fuel,
    CheckCircle,
    Clock,
    AlertCircle,
    TrendingUp,
    Play,
    StopCircle,
    List,
    Map as MapIcon,
    Camera,
    Eye,
    History,
    ExternalLink,
    Phone,
    User,
    Truck,
    CreditCard,
    Calendar,
    StickyNote,
    ChevronDown,
    CheckCircle2,
    Upload,
    X,
    QrCode,
    Receipt,
    GripVertical,
    Printer,
    RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { computeCollectionAmounts, isCreditOrder } from '../utils/payments';
import RouteMapView from '../components/RouteMapView';
import OrderDetailsModal from '../components/OrderDetailsModal';
import OrderTimelineModal from '../components/OrderTimelineModal';
import EnhancedFailedDeliveryModal from '../components/EnhancedFailedDeliveryModal';
import RouteExpensesModal from '../components/RouteExpensesModal';
import AdditionalStopsModal from '../components/AdditionalStopsModal';
import StopManagementModal from '../components/StopManagementModal';
import QRScanner from '../components/QRScanner';
import AddressEditModal from '../components/AddressEditModal'; // 🆕 Address Edit
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SortableOrderCard from '../components/SortableOrderCard';
import SortableAdditionalStopCard from '../components/SortableAdditionalStopCard';

import GeocodingResolutionModal from '../components/GeocodingResolutionModalV2'; // 🆕 (Cache Buster)

const MessengerRoutePage = () => {
    const { user } = useAuth();
    const [route, setRoute] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('list'); // 'list' or 'map'
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showExpensesModal, setShowExpensesModal] = useState(false); // 🆕 Route expenses
    const [showStopsModal, setShowStopsModal] = useState(false); // 🆕 Additional stops
    const [showGeocodingModal, setShowGeocodingModal] = useState(false); // 🆕 Geocoding modal
    const [geocodingFailures, setGeocodingFailures] = useState([]); // 🆕 Geocoding failures
    const [isGeocoding, setIsGeocoding] = useState(false); // 🆕 Geocoding loading state
    const [selectedOrderForFailed, setSelectedOrderForFailed] = useState(null); // 🆕 Enhanced failed delivery
    const [selectedOrderForAddressEdit, setSelectedOrderForAddressEdit] = useState(null); // 🆕 Address edit
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [selectedOrderTimeline, setSelectedOrderTimeline] = useState(null);
    const [selectedStopForManagement, setSelectedStopForManagement] = useState(null);
    const [showItems, setShowItems] = useState(false);
    const [showQRScanner, setShowQRScanner] = useState(false);
    const [closeData, setCloseData] = useState({
        declaredCash: '',
        verificationCode: ''
    });

    // 🆕 Drag-and-drop configuration
    const [isReordering, setIsReordering] = useState(false);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Tracking de ubicación en tiempo real
    useEffect(() => {
        let interval;
        if (route && route.status === 'in_progress') {
            const reportLocation = () => {
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const { latitude, longitude } = position.coords;
                            api.post(`/routes/${route.id}/location`, { latitude, longitude })
                                .catch(err => console.error('Error reporting location:', err));
                        },
                        (error) => {
                            // Silencioso para no molestar al mensajero, solo logs
                            console.warn('Geolocation error:', error);
                        },
                        {
                            enableHighAccuracy: true,
                            timeout: 10000,
                            maximumAge: 30000
                        }
                    );
                }
            };

            // Reportar inmediatamente
            reportLocation();

            // Intervalo de 2 minutos para no agotar batería pero tener trazabilidad funcional
            interval = setInterval(reportLocation, 120000);
        }
        return () => clearInterval(interval);
    }, [route?.status, route?.id]);

    useEffect(() => {
        loadActiveRoute();
    }, []);

    const loadActiveRoute = async () => {
        try {
            // Solo mostrar spinner de carga completa en la carga inicial (sin ruta previa)
            // En recargas posteriores (ej: después de entregar), NO mostrar spinner
            // para preservar la posición de scroll del mensajero
            if (!route) setLoading(true);
            const response = await getActiveRoute();

            if (response.success && response.data) {
                setRoute(response.data);
            } else {
                setRoute(null);
            }
        } catch (err) {
            console.error('Error cargando ruta:', err);
            if (err.response?.status !== 404) {
                toast.error('Error cargando ruta activa');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStartRoute = async () => {
        try {
            const response = await startRoute(route.id);
            if (response.success) {
                toast.success('Ruta iniciada');
                loadActiveRoute();
            }
        } catch (err) {
            console.error('Error iniciando ruta:', err);
            toast.error(err.response?.data?.message || 'Error iniciando ruta');
        }
    };

    const handleReOptimizeRoute = async () => {
        try {
            const confirmOptimize = window.confirm(
                '¿Estás seguro que deseas re-optimizar la ruta basándote en los pedidos pendientes? Esto reordenará las paradas para minimizar distancia.'
            );

            if (!confirmOptimize) return;

            setLoading(true);
            const response = await api.put(`/routes/${route.id}/re-optimize`);

            if (response.data.success) {
                toast.success('✅ Ruta re-optimizada exitosamente');
                await loadActiveRoute(); // Recargar ruta con nuevo orden
            } else {
                toast.error(response.data.message || 'Error al re-optimizar');
            }
        } catch (error) {
            console.error('Error re-optimizando ruta:', error);
            toast.error(error.response?.data?.message || 'Error al re-optimizar la ruta');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseRoute = async () => {
        try {
            // Validar datos
            if (!closeData.declaredCash) {
                toast.error('Ingresa el efectivo declarado');
                return;
            }

            const response = await closeRoute(route.id, {
                declaredCash: parseFloat(closeData.declaredCash),
                verificationCode: closeData.verificationCode
            });

            if (response.success) {
                toast.success('Ruta cerrada exitosamente');
                setShowCloseModal(false);
                loadActiveRoute();
            }
        } catch (err) {
            console.error('Error cerrando ruta:', err);
            toast.error(err.response?.data?.message || 'Error cerrando ruta');
        }
    };



    // 🆕 Handler para geocodificar la ruta (Manual o Automático)
    const handleGeocodeRoute = async () => {
        setIsGeocoding(true);
        setGeocodingFailures([]);

        try {
            const response = await api.post(`/routes/${route.id}/geocode`);
            const data = response.data;

            if (data.success) {
                if (data.failed > 0) {
                    setGeocodingFailures(data.results.filter(r => !r.success));
                    setShowGeocodingModal(true);
                    toast.error(`${data.failed} pedidos no se pudieron localizar. Revisa la lista.`);
                } else {
                    toast.success('Geocodificación completada exitosamente');
                    loadActiveRoute(); // Recargar mapa
                }
            } else {
                toast.error(data.message || 'Error al geocodificar');
            }
        } catch (error) {
            console.error('Error geocodificando ruta:', error);
            toast.error('Error de conexión. Intenta nuevamente.');
        } finally {
            setIsGeocoding(false);
        }
    };


    // 🆕 [PHASE 1] Handler para entrega fallida con evidencia
    const handleEnhancedFailedDelivery = async (failureData, addressCorrectionData) => {
        try {
            const response = await markOrderFailedV2(selectedOrderForFailed.id, failureData);

            if (response.success && addressCorrectionData) {
                try {
                    await correctOrderAddress(selectedOrderForFailed.id, {
                        ...addressCorrectionData,
                        action: 'cancel'
                    });
                } catch (addrErr) {
                    console.error('Error correcting address:', addrErr);
                    toast.error('Fallo registrado, pero no se pudo guardar corrección de dirección');
                }
            }

            if (response.success) {
                toast.success('Entrega marcada como fallida con evidencia');
                setSelectedOrderForFailed(null);
                loadActiveRoute();
            }
        } catch (error) {
            console.error('Error marking failed delivery:', error);
            throw error;
        }
    };

    // 🆕 Handler para actualizar dirección directa
    const handleAddressUpdate = async (orderId, newAddress, notes) => {
        await updateOrderAddress(orderId, newAddress, notes);
        // Recargar ruta para reflejar cambios (y posiblemente resetear coordenadas)
        loadActiveRoute();
    };

    // 🆕 Handler para reordenar paradas
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Construir lista unificada actual para encontrar índices
        const currentItems = [
            ...(route.orders || []).map(o => ({ ...o, dndId: o.id })),
            ...(route.additionalStops || [])
                .filter(s => s.status === 'pending')
                .map(s => ({ ...s, dndId: `stop-${s.id}` }))
        ].sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));

        const oldIndex = currentItems.findIndex(item => item.dndId === active.id);
        const newIndex = currentItems.findIndex(item => item.dndId === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        // Mover en el array unificado
        const movedItems = arrayMove(currentItems, oldIndex, newIndex);

        // Actualizar estados locales (dividir de nuevo)
        const updatedOrders = movedItems.filter(item => !item.dndId.toString().startsWith('stop-'));
        const updatedStops = movedItems.filter(item => item.dndId.toString().startsWith('stop-'));

        // Nota: Solo actualizamos el estado local para feedback visual inmediato
        // pero luego recargamos de verdad con loadActiveRoute() tras el backend.
        setRoute(prev => ({
            ...prev,
            orders: updatedOrders,
            additionalStops: [
                ...updatedStops,
                ...(prev.additionalStops || []).filter(s => s.status !== 'pending')
            ]
        }));

        // Guardar en backend
        try {
            setIsReordering(true);
            const orderIds = movedItems.map(item => item.dndId);
            const response = await api.put(`/routes/${route.id}/reorder`, { orderIds });

            if (response.data.success) {
                toast.success('Orden actualizado');
                loadActiveRoute(); // Recargar para obtener números de secuencia actualizados
            }
        } catch (error) {
            console.error('Error reordenando ruta:', error);
            toast.error('Error al actualizar el orden');
            loadActiveRoute(); // Revertir cambios
        } finally {
            setIsReordering(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando ruta...</p>
                </div>
            </div>
        );
    }

    if (!route) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Navigation className="w-10 h-10 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        No tienes ruta asignada
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Actualmente no tienes ninguna ruta activa para hoy.
                        Contacta a Logística para que te asignen pedidos.
                    </p>
                    <button
                        onClick={loadActiveRoute}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Actualizar
                    </button>
                </div>
            </div>
        );
    }

    const deliveredOrders = route.orders?.filter(o => o.delivery_status === 'entregado' || o.deliveryStatus === 'delivered').length || 0;
    const totalOrders = route.orders?.length || 0;
    const completedExtraStops = route.additionalStops?.filter(s => s.status === 'completed').length || 0;
    const totalExtraStops = route.additionalStops?.length || 0;
    const progress = (totalOrders + totalExtraStops) > 0 ? ((deliveredOrders + completedExtraStops) / (totalOrders + totalExtraStops)) * 100 : 0;

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header con resumen de ruta - ULTRA COMPACTO */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 sm:p-4">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg sm:text-2xl font-black flex items-center gap-1.5">
                                <Navigation className="w-5 h-5" />
                                Ruta #{route.routeNumber}
                            </h1>
                            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${route.status === 'open' ? 'bg-yellow-500' :
                                route.status === 'in_progress' ? 'bg-green-500' :
                                    route.status === 'closed' ? 'bg-gray-500' :
                                        'bg-blue-500'
                                }`}>
                                {route.status === 'open' ? 'LISTA' :
                                    route.status === 'in_progress' ? 'EN PROGRESO' :
                                        route.status === 'closed' ? 'CERRADA' : route.status}
                            </div>
                        </div>
                        <div className="text-right text-[10px] text-blue-100 font-medium">
                            {new Date(route.routeDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
                        </div>
                    </div>

                    {/* Stats Compactos y Horizontales */}
                    <div className={`grid ${totalExtraStops > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-1.5 sm:gap-2`}>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 flex flex-col items-center">
                            <span className="text-[9px] text-blue-100 uppercase font-bold">Pedidos</span>
                            <p className="text-[12px] sm:text-sm font-black">{deliveredOrders}/{totalOrders}</p>
                        </div>
                        {totalExtraStops > 0 && (
                            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 flex flex-col items-center border border-purple-400/30">
                                <span className="text-[9px] text-purple-100 uppercase font-bold">Extras</span>
                                <p className="text-[12px] sm:text-sm font-black">{completedExtraStops}/{totalExtraStops}</p>
                            </div>
                        )}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 flex flex-col items-center">
                            <span className="text-[9px] text-blue-100 uppercase font-bold">Cobrar</span>
                            <p className="text-[12px] sm:text-sm font-black">
                                ${route.orders?.reduce((sum, order) => {
                                    // [FIX] Usar calculated_balance_due si existe
                                    if (order.calculated_balance_due !== undefined && order.calculated_balance_due !== null) {
                                        return sum + Number(order.calculated_balance_due);
                                    }
                                    const total = (parseFloat(order.total_amount) || 0) + (parseFloat(order.int_amount) || 0);
                                    const paid = parseFloat(order.total_paid) || 0;
                                    return sum + Math.max(0, total - paid);
                                }, 0).toLocaleString('es-CO')}
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 flex flex-col items-center">
                            <span className="text-[9px] text-blue-100 uppercase font-bold">Distancia</span>
                            <p className="text-[12px] sm:text-sm font-black">{parseFloat(route.totalDistance).toFixed(1)} km</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Botones de acción - Más Compactos */}
            <div className="max-w-4xl mx-auto px-3 -mt-2 mb-4">
                <div className="bg-white rounded-lg shadow-md p-2 flex flex-col gap-2">
                    <div className="flex gap-2">
                        {route.status === 'open' && (
                            <button
                                onClick={handleStartRoute}
                                className="flex-1 bg-green-600 text-white py-2 px-3 rounded-md font-bold text-sm hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5"
                            >
                                <Play className="w-4 h-4" />
                                INICIAR RUTA
                            </button>
                        )}
                        {route.status === 'in_progress' && (
                            <button
                                onClick={() => setShowCloseModal(true)}
                                className="flex-1 bg-red-600 text-white py-2 px-3 rounded-md font-bold text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-1.5"
                            >
                                <StopCircle className="w-4 h-4" />
                                CERRAR RUTA
                            </button>
                        )}
                    </div>
                    {/* Fila secundaria de botones */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {(route.status === 'in_progress' || route.status === 'closed') && (
                            <button
                                onClick={() => setShowExpensesModal(true)}
                                className="bg-gray-100 text-gray-700 py-2 rounded-md font-bold text-[10px] uppercase hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                            >
                                <Receipt className="w-3.5 h-3.5" />
                                Gastos
                            </button>
                        )}
                        {(route.status === 'in_progress' || route.status === 'closed') && (
                            <button
                                onClick={() => setShowStopsModal(true)}
                                className="bg-gray-100 text-gray-700 py-2 rounded-md font-bold text-[10px] uppercase hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                            >
                                <MapPin className="w-3.5 h-3.5" />
                                Paradas
                            </button>
                        )}
                        {(route.status === 'in_progress' || (route.status === 'open' && route.isLocked)) && route.orders && route.orders.filter(o => o.messengerStatus !== 'delivered' && o.messengerStatus !== 'failed').length > 1 && (
                            <button
                                onClick={handleReOptimizeRoute}
                                disabled={loading}
                                className="bg-blue-50 text-blue-700 py-2 rounded-md font-bold text-[10px] uppercase border border-blue-200 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                            >
                                <TrendingUp className="w-3.5 h-3.5" />
                                Optimizar
                            </button>
                        )}
                        {(route.status === 'in_progress' || route.status === 'open') && (
                            <button
                                onClick={handleGeocodeRoute}
                                disabled={isGeocoding}
                                className="bg-blue-50 text-blue-700 py-2 rounded-md font-bold text-[10px] uppercase border border-blue-200 hover:bg-blue-100 transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                            >
                                <MapPin className={`w-3.5 h-3.5 ${isGeocoding ? 'animate-bounce' : ''}`} />
                                {isGeocoding ? '...' : 'Geocodificar'}
                            </button>
                        )}
                        <button
                            onClick={() => printRouteSheet(route)}
                            className="bg-gray-100 text-gray-700 py-2 rounded-md font-bold text-[10px] uppercase hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                        >
                            <Printer className="w-3.5 h-3.5" />
                            Imprimir
                        </button>
                    </div>
                </div>
            </div>

            {/* Pestañas: Lista / Mapa + Info de la ruta */}
            <div className="max-w-4xl mx-auto px-6 mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 flex gap-2">
                    <button
                        onClick={() => setActiveTab('list')}
                        className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors flex items-center justify-center gap-2 ${activeTab === 'list'
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        <List className="w-5 h-5" />
                        Lista de Pedidos
                    </button>
                    <button
                        onClick={() => setActiveTab('map')}
                        className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors flex items-center justify-center gap-2 ${activeTab === 'map'
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        <MapIcon className="w-5 h-5" />
                        Ver Mapa
                    </button>

                    {/* Info compacta de la ruta */}
                    {activeTab === 'map' && (
                        <div className="hidden sm:flex items-center gap-4 px-4 text-sm text-gray-700 border-l border-gray-300">
                            <div className="flex items-center gap-1.5" title="Pedidos">
                                <Package className="w-4 h-4 text-blue-600" />
                                <span className="font-semibold">{totalOrders}</span>
                            </div>
                            {totalExtraStops > 0 && (
                                <div className="flex items-center gap-1.5" title="Paradas Extras">
                                    <MapPin className="w-4 h-4 text-purple-600" />
                                    <span className="font-semibold">{totalExtraStops}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-1.5" title="Distancia Estimada">
                                <Navigation className="w-4 h-4 text-green-600" />
                                <span className="font-semibold">{parseFloat(route?.totalDistance || 0).toFixed(1)} km</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Contenido según pestaña activa */}
            <div className="max-w-4xl mx-auto px-6">
                {activeTab === 'list' ? (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">
                                Pedidos ({totalOrders})
                            </h2>
                            {(route.status === 'open' || route.status === 'in_progress') && (
                                <span className="text-xs text-gray-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                                    ⋮⋮ Arrastra para reordenar
                                </span>
                            )}
                        </div>

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            {(() => {
                                // Lista unificada de paradas pendientes y pedidos
                                const unifiedItems = [
                                    ...(route.orders || []).map(o => ({ ...o, dndId: o.id, type: 'order' })),
                                    ...(route.additionalStops || [])
                                        .map(s => ({ ...s, dndId: `stop-${s.id}`, type: 'stop' }))
                                ].sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));

                                return (
                                    <div className="space-y-2">
                                        {/* INICIO: Bodega */}
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3 shadow-sm">
                                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                                <Navigation className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold text-green-900 text-sm">INICIO: BODEGA</h3>
                                                <p className="text-xs text-green-700">Cra 7B # 26 Sur 29</p>
                                            </div>
                                        </div>

                                        <SortableContext
                                            items={unifiedItems.map(item => item.dndId)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <div className="space-y-2">
                                                {unifiedItems.map((item, index) => {
                                                    const displaySequence = index + 1;
                                                    if (item.type === 'order') {
                                                        const isDelivered = item.delivery_status === 'entregado' || item.deliveryStatus === 'delivered' || item.deliveryStatus === 'entregado';
                                                        const isFailed = item.delivery_status === 'failed' || item.delivery_status === 'fallido' || item.deliveryStatus === 'delivery_failed' || item.messenger_status === 'delivery_failed';
                                                        const isReturned = item.delivery_status === 'returned';
                                                        return (
                                                            <SortableOrderCard
                                                                key={item.id}
                                                                order={{ ...item, sequenceNumber: displaySequence }}
                                                                route={route}
                                                                isDelivered={isDelivered}
                                                                isFailed={isFailed}
                                                                isReturned={isReturned}
                                                                setSelectedOrderDetails={setSelectedOrderDetails}
                                                                setSelectedOrderForFailed={setSelectedOrderForFailed}
                                                                setSelectedOrderTimeline={setSelectedOrderTimeline}
                                                                setActiveTab={setActiveTab}
                                                                onEditAddress={setSelectedOrderForAddressEdit}

                                                            />
                                                        );
                                                    } else {
                                                        return (
                                                            <SortableAdditionalStopCard
                                                                key={item.dndId}
                                                                stop={{ ...item, sequenceNumber: displaySequence }}
                                                                onManage={(stop) => setSelectedStopForManagement(stop)}
                                                            />
                                                        );
                                                    }
                                                })}
                                            </div>
                                        </SortableContext>

                                        {/* FIN: Bodega */}
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3 shadow-sm mt-4">
                                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                                <CheckCircle2 className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold text-blue-900 text-sm">FIN: REGRESO A BODEGA</h3>
                                                <p className="text-xs text-blue-700">Guardar vehículo y liquidar ruta</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </DndContext>
                    </>
                ) : (
                    <RouteMapView
                        route={route}
                        onOrderSelect={(order) => {
                            if (route.status === 'in_progress' && (order.delivery_status !== 'entregado' && order.deliveryStatus !== 'delivered')) {
                                setSelectedOrderTimeline({ ...order, mode: 'delivery' });
                            }
                        }}
                    />
                )}
            </div>

            {/* Modal de cerrar ruta */}
            {
                showCloseModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-2xl font-bold text-gray-900">Cerrar Ruta</h2>
                                <p className="text-gray-600 mt-1">
                                    Registra el efectivo recaudado y los gastos de la ruta
                                </p>

                            </div>

                            <div className="p-6 space-y-6">
                                {/* Efectivo declarado */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Efectivo Declarado *
                                    </label>
                                    <div className="relative">
                                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="number"
                                            value={closeData.declaredCash}
                                            onChange={(e) => setCloseData(prev => ({ ...prev, declaredCash: e.target.value }))}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0"
                                        />
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Esperado: ${parseFloat(route.expectedCash || 0).toLocaleString('es-CO')}
                                    </p>
                                </div>

                                {/* Verificación QR / Código */}
                                <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100">
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="text-sm font-bold text-blue-900 flex items-center gap-2">
                                            <QrCode className="w-5 h-5" />
                                            Verificación de Llegada a Bodega
                                        </label>
                                        <button
                                            onClick={() => setShowQRScanner(true)}
                                            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-all flex items-center gap-2 shadow-sm"
                                        >
                                            <Camera className="w-4 h-4" />
                                            ESCANEAR QR
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={closeData.verificationCode}
                                            onChange={(e) => setCloseData(prev => ({ ...prev, verificationCode: e.target.value }))}
                                            className="w-full px-4 py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white font-mono text-center tracking-widest text-lg"
                                            placeholder="Código de verificación"
                                        />
                                    </div>
                                    <p className="text-xs text-blue-600 mt-2 text-center font-medium">
                                        Escanea el QR o ingresa el código manual para confirmar.
                                    </p>
                                </div>
                            </div>

                            <div className="p-6 border-t border-gray-200 flex gap-3">
                                <button
                                    onClick={() => setShowCloseModal(false)}
                                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCloseRoute}
                                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                                >
                                    Cerrar Ruta
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal de Detalles del Pedido - Mejorado */}
            <OrderDetailsModal
                isOpen={!!selectedOrderDetails}
                order={selectedOrderDetails}
                onClose={() => {
                    setSelectedOrderDetails(null);
                    setShowItems(false);
                }}
            />

            {/* Modal Unificado: Historial + Registro de Entrega */}
            <OrderTimelineModal
                isOpen={!!selectedOrderTimeline}
                onClose={() => setSelectedOrderTimeline(null)}
                initialMode={selectedOrderTimeline?.mode || 'history'}
                initialStep={selectedOrderTimeline?.step || 1}
                onSuccess={loadActiveRoute}
                order={selectedOrderTimeline ? {
                    ...selectedOrderTimeline,
                    order_number: selectedOrderTimeline.orderNumber
                } : null}
            />

            {/* 🆕 [PHASE 1] Modal de Entrega Fallida con Evidencia */}
            {
                selectedOrderForFailed && (
                    <EnhancedFailedDeliveryModal
                        order={selectedOrderForFailed}
                        onClose={() => setSelectedOrderForFailed(null)}
                        onSubmit={handleEnhancedFailedDelivery}
                    />
                )
            }

            {/* 🆕 [PHASE 12] Modal de Resolución de Geocodificación */}
            <GeocodingResolutionModal
                isOpen={showGeocodingModal}
                onClose={() => setShowGeocodingModal(false)}
                failures={geocodingFailures}
                routeId={route?.id}
                onSuccess={loadActiveRoute}
            />

            {/* 💰 [PHASE 2] Modal de Gastos de Ruta */}
            <RouteExpensesModal
                isOpen={showExpensesModal}
                onClose={() => setShowExpensesModal(false)}
                routeId={route?.id}
            />

            {/* 📍 [PHASE 3] Modal de Paradas Adicionales */}
            <AdditionalStopsModal
                isOpen={showStopsModal}
                onClose={() => setShowStopsModal(false)}
                routeId={route?.id}
                isMessenger={true}
            />

            {/* 🆕 [PHASE 9] Modal de Gestión de Parada Extra */}
            <StopManagementModal
                isOpen={!!selectedStopForManagement}
                stop={selectedStopForManagement}
                routeId={route?.id}
                onClose={() => setSelectedStopForManagement(null)}
                onSuccess={loadActiveRoute}
            />

            {/* 🆕 Modal para Editar Dirección */}
            <AddressEditModal
                isOpen={!!selectedOrderForAddressEdit}
                onClose={() => setSelectedOrderForAddressEdit(null)}
                order={selectedOrderForAddressEdit}
                onUpdate={handleAddressUpdate}
            />

            {/* 🆕 QR Scanner Overlay */}
            {showQRScanner && (
                <QRScanner
                    onScan={(code) => {
                        setCloseData(prev => ({ ...prev, verificationCode: code }));
                        setShowQRScanner(false);
                        toast.success('¡Código capturado!');
                    }}
                    onClose={() => setShowQRScanner(false)}
                />
            )}
        </div>
    );
};

export default MessengerRoutePage;
